import React, { useEffect } from "react";
import { MenuLinks } from "./Navbar";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../config/translation/LanguageSwitcher";
import { HiX, HiHome, HiViewGrid, HiUserGroup, HiMail } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

const menuIcons = {
	"navbar.home": HiHome,
	"navbar.services": HiViewGrid,
	"navbar.about": HiUserGroup,
};

const ResponsiveMenu = ({ showMenu, closeMenu }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Prevent body scroll when menu is open
	useEffect(() => {
		if (showMenu) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [showMenu]);

	const handleContactClick = () => {
		navigate("/contact-us");
		closeMenu();
	};

	const handleLinkClick = (link) => {
		navigate("/");
		closeMenu();
		setTimeout(() => {
			const section = document.getElementById(link.replace("#", ""));
			if (section) {
				section.scrollIntoView({ behavior: "smooth" });
			}
		}, 100);
	};

	return (
		<>
			{/* Backdrop */}
			<div
				className={`fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 transition-all duration-300 md:hidden ${
					showMenu ? "opacity-100 visible" : "opacity-0 invisible"
				}`}
				onClick={closeMenu}
			/>

			{/* Menu Panel */}
			<div
				className={`fixed top-0 right-0 h-full w-[85%] max-w-xs z-50 md:hidden transform transition-transform duration-300 ease-out ${
					showMenu ? "translate-x-0" : "translate-x-full"
				}`}
			>
				<div className="h-full bg-white dark:bg-slate-900 shadow-2xl flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-slate-800">
						<h2 className="text-lg font-bold text-slate-800 dark:text-white">Menu</h2>
						<div className="flex items-center gap-2">
							<LanguageSwitcher />
							<button
								onClick={closeMenu}
								className="p-2 rounded-xl bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
								aria-label="Close menu"
							>
								<HiX className="text-xl text-gray-600 dark:text-gray-300" />
							</button>
						</div>
					</div>

					{/* Navigation Links */}
					<nav className="flex-1 p-4 overflow-y-auto">
						<ul className="space-y-1">
							{MenuLinks.map(({ id, name, link }, index) => {
								const Icon = menuIcons[name] || HiViewGrid;
								return (
									<li
										key={id}
										style={{
											opacity: showMenu ? 1 : 0,
											transform: showMenu ? "translateX(0)" : "translateX(20px)",
											transition: `all 0.3s ease ${index * 0.05 + 0.1}s`,
										}}
									>
										<button
											onClick={() => handleLinkClick(link)}
											className="w-full flex items-center gap-4 px-4 py-4 rounded-xl text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-accent/10 hover:text-accent active:bg-accent/20 transition-all duration-200"
										>
											<span className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center">
												<Icon className="text-lg text-accent" />
											</span>
											{t(name)}
										</button>
									</li>
								);
							})}
						</ul>

						{/* Contact Button */}
						<div
							className="mt-6"
							style={{
								opacity: showMenu ? 1 : 0,
								transform: showMenu ? "translateY(0)" : "translateY(10px)",
								transition: "all 0.3s ease 0.25s",
							}}
						>
							<button
								onClick={handleContactClick}
								className="w-full flex items-center justify-center gap-3 primary-btn text-base py-4"
							>
								<HiMail className="text-xl" />
								{t("navbar.contact")}
							</button>
						</div>
					</nav>

					{/* Footer */}
					<div className="p-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/50">
						<p className="text-xs text-gray-500 dark:text-gray-400 text-center">
							&copy; {new Date().getFullYear()} K.PREMTIS
						</p>
					</div>
				</div>
			</div>
		</>
	);
};

export default ResponsiveMenu;
